import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
  Clock, Award, CheckCircle2, Play, FileText, ClipboardCheck
} from "lucide-react";

/**
 * VideoDialog - YouTube video player modal for course content
 * Shows video, learning objectives, and progression options
 */
export default function VideoDialog({
  isOpen,
  onClose,
  course,
  isCompleted,
  videoWatched,
  onMarkVideoWatched,
  onStartQuiz,
  onViewCertificate
}) {
  if (!course) return null;

  const getYouTubeUrl = () => {
    let url = `https://www.youtube.com/embed/${course.youtubeId}?rel=0&modestbranding=1`;
    if (course.startTime) {
      url += `&start=${course.startTime}`;
    }
    return url;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0">
        <DialogHeader className="p-6 pb-2">
          <div className="flex items-center gap-2 mb-2">
            {course.elnecModule && (
              <Badge className="bg-indigo-600">ELNEC Module {course.elnecModule}</Badge>
            )}
            <Badge variant="outline">
              <Award className="w-3 h-3 mr-1" />
              {course.ceuCredits} CEU Credits
            </Badge>
          </div>
          <DialogTitle>{course.title}</DialogTitle>
          <DialogDescription>{course.description}</DialogDescription>
        </DialogHeader>
        
        <div className="aspect-video bg-black">
          <iframe
            width="100%"
            height="100%"
            src={getYouTubeUrl()}
            title={course.title}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>

        <div className="p-6 pt-4">
          <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {course.duration}
            </span>
            <Badge variant="outline">{course.level}</Badge>
            <span>Instructor: {course.instructor}</span>
          </div>

          <div className="mb-4">
            <h4 className="font-medium mb-2">Learning Objectives</h4>
            <ul className="grid md:grid-cols-2 gap-2">
              {course.objectives?.map((obj, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  {obj}
                </li>
              ))}
            </ul>
          </div>

          {/* Quiz requirement notice */}
          {!isCompleted && (
            <Alert className="mb-4">
              <ClipboardCheck className="w-4 h-4" />
              <AlertDescription>
                <strong>80% quiz score required</strong> to earn {course.ceuCredits} CEU credits. 
                Watch the video completely, then take the assessment.
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-3">
            {isCompleted ? (
              <>
                <Button className="flex-1" variant="outline" disabled>
                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> 
                  Completed - {course.ceuCredits} CEU Earned
                </Button>
                <Button variant="outline" onClick={() => {
                  onClose();
                  onViewCertificate?.(course);
                }}>
                  <FileText className="w-4 h-4 mr-2" />
                  Certificate
                </Button>
              </>
            ) : (
              <>
                <Button 
                  className="flex-1"
                  variant={videoWatched ? "default" : "outline"}
                  onClick={() => {
                    onMarkVideoWatched(course.id);
                    if (videoWatched) {
                      onClose();
                      onStartQuiz?.(course);
                    }
                  }}
                >
                  {videoWatched ? (
                    <><ClipboardCheck className="w-4 h-4 mr-2" /> Take Quiz to Earn CEU</>
                  ) : (
                    <><CheckCircle2 className="w-4 h-4 mr-2" /> I've Watched This Video</>
                  )}
                </Button>
                {!videoWatched && (
                  <Button variant="outline" onClick={onClose}>
                    Close
                  </Button>
                )}
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
